import { ArrowUpRight, ArrowDownRight, Minus, AlertOctagon, Target, Shield, Zap } from "lucide-react";
import { useValidationStatus, DescriptiveChip } from "@/components/Validation";

const SIGNAL_STYLES = {
  LONG: {
    icon: ArrowUpRight,
    color: "text-emerald-300",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/50",
    accent: "#10B981",
    label: "LONG",
  },
  SHORT: {
    icon: ArrowDownRight,
    color: "text-red-300",
    bg: "bg-red-500/10",
    border: "border-red-500/50",
    accent: "#EF4444",
    label: "SHORT",
  },
  WAIT: {
    icon: Minus,
    color: "text-slate-300",
    bg: "bg-slate-500/10",
    border: "border-slate-500/50",
    accent: "#94A3B8",
    label: "WAIT",
  },
};

function fmt(v, d = 2) {
  if (v == null || Number.isNaN(v)) return "—";
  const n = Number(v);
  if (Math.abs(n) >= 1000) return n.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
  return n.toFixed(d);
}

function ScoreBar({ score, side, dim }) {
  const w = Math.max(0, Math.min(100, score ?? 0));
  const color = side === "long" ? "bg-emerald-500" : "bg-red-500";
  return (
    <div className={`w-full h-1.5 bg-[#151C2C] relative ${dim ? "opacity-60" : ""}`}>
      <div className={`absolute inset-y-0 left-0 ${color}`} style={{ width: `${w}%` }} />
    </div>
  );
}

export default function SignalCard({ result }) {
  const validation = useValidationStatus();
  const swingVerdict = validation?.swing?.verdict;
  const swingValidated = swingVerdict === "VALIDATED";
  const swingDescriptive = swingVerdict === "MARGINAL" || swingVerdict === "NULL";
  if (!result) return null;
  const s = SIGNAL_STYLES[result.signal] || SIGNAL_STYLES.WAIT;
  const Icon = s.icon;
  const isDirectional = result.signal === "LONG" || result.signal === "SHORT";
  const dataQ = result.status === "DATA_QUALITY_WARNING";
  const primaryScore = result.signal === "SHORT" ? result.short_score : result.long_score;
  const primaryLabel = result.signal === "SHORT" ? "Short Score" : "Long Score";
  const plan = result.entry_plan;

  return (
    <div
      data-testid="signal-card"
      className={`border ${s.border} ${s.bg} relative`}
    >
      <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${s.accent}90, transparent)` }} />
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(260px,340px)_1fr] gap-0">
        {/* Left: signal badge */}
        <div className="p-5 border-r border-[#1E293B]">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="h-3.5 w-3.5 text-cyan-400" />
            <span className="mono text-[10px] uppercase tracking-widest text-slate-400">
              Quant Signal · v{result.engine_version || "1.0.0"}
            </span>
          </div>
          <div className={`flex items-center gap-3 ${s.color}`}>
            <Icon className="h-9 w-9" strokeWidth={2.5} />
            <div className="flex flex-col leading-none">
              <span data-testid="signal-label" className="mono text-3xl font-extrabold tracking-tight">{s.label}</span>
              <span className="mono text-[11px] uppercase tracking-widest text-slate-400 mt-1">
                {result.strength_band || "—"}
              </span>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-[#1E293B] space-y-3">
            <div>
              <div className="flex justify-between items-baseline">
                <span className="mono text-[10px] uppercase tracking-widest text-slate-500">Long score</span>
                <span data-testid="signal-long-score" className="mono text-lg font-bold text-emerald-300 tabular-nums">
                  {fmt(result.long_score, 1)}<span className="text-slate-600 text-xs">/100</span>
                </span>
              </div>
              <ScoreBar score={result.long_score} side="long" dim={result.signal === "SHORT"} />
            </div>
            <div>
              <div className="flex justify-between items-baseline">
                <span className="mono text-[10px] uppercase tracking-widest text-slate-500">Short score</span>
                <span data-testid="signal-short-score" className="mono text-lg font-bold text-red-300 tabular-nums">
                  {fmt(result.short_score, 1)}<span className="text-slate-600 text-xs">/100</span>
                </span>
              </div>
              <ScoreBar score={result.short_score} side="short" dim={result.signal === "LONG"} />
            </div>
            <div className="pt-2 flex items-center justify-between mono text-[10px] uppercase tracking-widest">
              <span className="text-slate-500">Confidence</span>
              {swingDescriptive ? (
                <DescriptiveChip testid="swing-descriptive-chip" />
              ) : (
                <span data-testid="signal-confidence" className={
                  result.confidence?.tier === "high" ? "text-emerald-300" :
                  result.confidence?.tier === "medium" ? "text-cyan-300" :
                  result.confidence?.tier === "n/a" ? "text-slate-500" : "text-amber-300"
                }>
                  {result.confidence?.tier || "—"} · {result.confidence?.basis || "structural"}
                </span>
              )}
            </div>
            {swingValidated && validation?.swing?.as_of && (
              <div data-testid="signal-validated-note" className="mono text-[10px] uppercase tracking-widest text-emerald-400/70 pt-1">
                Walk-forward validated {validation.swing.as_of} · see Performance
              </div>
            )}
          </div>
        </div>

        {/* Right: entry plan or why-wait */}
        <div className="p-5">
          {dataQ && (
            <div className="flex items-start gap-2 mono text-xs text-amber-200 border border-amber-500/30 bg-amber-500/10 px-3 py-2 mb-3">
              <AlertOctagon className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
              <span data-testid="signal-data-quality">Data quality issue — {result.why_wait || result.reason || "history too thin"}</span>
            </div>
          )}

          {isDirectional && plan && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Target className="h-3.5 w-3.5 text-cyan-400" />
                <span className="mono text-[10px] uppercase tracking-widest text-slate-400">Entry Plan · {plan.entry_type}</span>
                <span className="mono text-[10px] uppercase tracking-widest text-slate-600 ml-auto">
                  ≈ {plan.expected_holding_days || "—"} days · R/R {fmt(plan.risk_reward_t1, 2)}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCell label="Ideal Entry" value={`$${fmt(plan.ideal_entry)}`} tone="cyan" testid="signal-ideal-entry" />
                <StatCell label="Entry Zone" value={`$${fmt(plan.entry_zone?.low)} – $${fmt(plan.entry_zone?.high)}`} tone="cyan" testid="signal-entry-zone" />
                <StatCell label="Do Not Chase" value={`$${fmt(plan.do_not_chase)}`} tone="amber" testid="signal-do-not-chase" />
                <StatCell label="Stop Loss" value={`$${fmt(plan.stop_loss)}`} tone="red" testid="signal-stop-loss" />
                <StatCell label="Target 1" value={`$${fmt(plan.targets?.t1)}`} tone="emerald" testid="signal-t1" />
                <StatCell label="Target 2" value={`$${fmt(plan.targets?.t2)}`} tone="emerald" testid="signal-t2" />
                <StatCell label="Target 3" value={`$${fmt(plan.targets?.t3)}`} tone="emerald" testid="signal-t3" />
                <StatCell label="R/R (to T1)" value={fmt(plan.risk_reward_t1, 2)} tone="cyan" />
              </div>
              <div className="mt-3 flex items-start gap-2 border border-red-500/30 bg-red-500/5 px-3 py-2 mono text-[11px] text-red-200">
                <Shield className="h-3.5 w-3.5 text-red-400 shrink-0 mt-0.5" />
                <span data-testid="signal-invalidation">
                  <span className="uppercase tracking-widest text-red-400 text-[10px] font-semibold mr-2">Invalidation</span>
                  {plan.invalidation?.human || `${plan.invalidation?.type} $${fmt(plan.invalidation?.price)}`}
                </span>
              </div>
            </div>
          )}

          {!isDirectional && !dataQ && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Minus className="h-3.5 w-3.5 text-slate-400" />
                <span className="mono text-[10px] uppercase tracking-widest text-slate-400">Why Wait</span>
              </div>
              <p data-testid="signal-why-wait" className="mono text-xs text-slate-300 leading-relaxed">
                {result.why_wait || "Neither directional score is strong enough to justify a trade."}
              </p>
              <div className="mt-4 pt-4 border-t border-[#1E293B] grid grid-cols-3 gap-3">
                <StatCell label="Long Required" value={`≥ 60 with +15 margin`} tone="emerald" />
                <StatCell label="Short Required" value={`≥ 60 with +15 margin`} tone="red" />
                <StatCell label="Current Margin" value={`${fmt(Math.abs((result.long_score || 0) - (result.short_score || 0)), 1)} pts`} tone="cyan" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCell({ label, value, tone = "slate", testid }) {
  const toneCls = {
    emerald: "text-emerald-300 border-emerald-500/30 bg-emerald-500/5",
    red: "text-red-300 border-red-500/30 bg-red-500/5",
    cyan: "text-cyan-300 border-cyan-500/30 bg-cyan-500/5",
    amber: "text-amber-300 border-amber-500/30 bg-amber-500/5",
    slate: "text-slate-200 border-slate-500/30 bg-slate-500/5",
  }[tone] || "text-slate-200 border-[#1E293B] bg-transparent";
  return (
    <div data-testid={testid} className={`border ${toneCls} px-2.5 py-2`}>
      <div className="mono text-[10px] uppercase tracking-widest text-slate-500 leading-none">
        {label}
      </div>
      <div className="mono text-sm font-bold tabular-nums leading-none mt-1.5">{value}</div>
    </div>
  );
}
